import React from 'react';

/**
 * Landing-system HeroShell.
 *
 * Provides the *outer* hero surface that both Kloutsale and Kloutforce share:
 *   - `relative overflow-hidden`
 *   - explicit text-white (no inheritance)
 *   - ambient radial gradient using landing tokens
 *   - max-w-7xl content container with consistent vertical rhythm
 *
 * The *inner* layout (centered vs split-grid, stat tiles, comparison cards)
 * stays brand-specific. Each app supplies children that match its messaging.
 *
 * Use `align="center"` for one-column, hero-style centered content
 * (Kloutforce sub-pages). Use `align="grid"` to opt out of the inner max-width
 * and place a custom grid (Kloutsale main hero).
 */
type Props = {
  align?: 'center' | 'grid';
  className?: string;
  innerClassName?: string;
  children: React.ReactNode;
};

export default function LandingHeroShell({
  align = 'center',
  className = '',
  innerClassName = '',
  children,
}: Props) {
  const inner =
    align === 'center'
      ? 'relative mx-auto max-w-5xl px-6 pb-16 pt-20 text-center md:pb-24 md:pt-28'
      : 'relative mx-auto max-w-7xl px-6 pb-16 pt-20 md:pb-24 md:pt-28';

  return (
    <section className={`relative overflow-hidden text-white ${className}`.trim()}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,hsl(var(--landing-accent)/0.22),transparent_36%),radial-gradient(circle_at_80%_18%,hsl(var(--landing-accent-ring)/0.16),transparent_30%)]" />
      <div className={`${inner} ${innerClassName}`.trim()}>{children}</div>
    </section>
  );
}